import React, { useState, useEffect, useMemo, useCallback, useRef, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import { useReactTable, getCoreRowModel, getSortedRowModel, getFilteredRowModel, getPaginationRowModel, flexRender } from '@tanstack/react-table'
import { fetchAllScreenerData, refreshScreenerCache } from '../services/screenerApi'
import ColumnConfigPopover from './ColumnConfigPopover'

// Utility functions for formatting
const formatCurrency = (value) => {
  if (value == null) return '-'
  const num = Number(value)
  if (Number.isNaN(num)) return value

  if (num >= 1e12) return `$${(num / 1e12).toFixed(1)}T`
  if (num >= 1e9) return `$${(num / 1e9).toFixed(1)}B`
  if (num >= 1e6) return `$${(num / 1e6).toFixed(1)}M`
  if (num >= 1e3) return `$${(num / 1e3).toFixed(1)}K`
  return `$${num.toFixed(2)}`
}

const formatPercent = (value) => {
  if (value == null) return '-'
  const num = Number(value)
  if (Number.isNaN(num)) return value
  return `${num.toFixed(2)}%`
}

const formatNumber = (value) => {
  if (value == null) return '-'
  const num = Number(value)
  if (Number.isNaN(num)) return '-'
  if (num >= 1e9) return `${(num / 1e9).toFixed(1)}B`
  if (num >= 1e6) return `${(num / 1e6).toFixed(1)}M`
  if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`
  return num.toFixed(2)
}

// AG-Grid-like filter popover
const FilterMenu = ({ column, dataType }) => {
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef(null)
  const buttonRef = useRef(null)
  const menuRef = useRef(null)
  const filter = column.getFilterValue() || null
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 })
  const isCategorical = dataType === 'categorical'

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      const insideWrapper = wrapperRef.current && wrapperRef.current.contains(e.target)
      const insideMenu = menuRef.current && menuRef.current.contains(e.target)
      if (!insideWrapper && !insideMenu) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  // Position the menu in a portal so it isn't clipped by overflow containers
  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return
    const rect = buttonRef.current.getBoundingClientRect()
    const menuWidth = 272
    const margin = 8
    let left = rect.left
    // Clamp to viewport
    if (left + menuWidth + margin > window.innerWidth) {
      left = window.innerWidth - menuWidth - margin
    }
    if (left < margin) left = margin
    const top = Math.min(rect.bottom + margin, window.innerHeight - margin)
    setCoords({ top, left, width: menuWidth })
  }, [])

  useLayoutEffect(() => {
    if (!open) return
    updatePosition()
    const onResize = () => updatePosition()
    window.addEventListener('resize', onResize)
    window.addEventListener('scroll', onResize, true)
    return () => {
      window.removeEventListener('resize', onResize)
      window.removeEventListener('scroll', onResize, true)
    }
  }, [open, updatePosition])

  const setFilter = (payload) => column.setFilterValue(payload)

  const op = filter?.op || (isCategorical ? 'in' : 'between')
  const value = filter?.value

  const opsNumerical = [
    { k: 'equals', l: 'Equals' },
    { k: 'notEquals', l: 'Does not equal' },
    { k: 'gt', l: 'Greater than' },
    { k: 'gte', l: 'Greater than or equal to' },
    { k: 'lt', l: 'Less than' },
    { k: 'lte', l: 'Less than or equal to' },
    { k: 'between', l: 'Between' },
    { k: 'blank', l: 'Blank' },
    { k: 'notBlank', l: 'Not blank' },
  ]
  const opsCategorical = [
    { k: 'contains', l: 'Contains' },
    { k: 'equals', l: 'Equals' },
    { k: 'notEquals', l: 'Does not equal' },
    { k: 'startsWith', l: 'Starts with' },
    { k: 'endsWith', l: 'Ends with' },
    { k: 'blank', l: 'Blank' },
    { k: 'notBlank', l: 'Not blank' },
  ]

  const isActive = isCategorical
    ? Array.isArray(value) && value.length > 0
    : !!filter && filter.op !== 'blank'

  return (
    <div className="relative inline-block" ref={wrapperRef}>
      <button
        type="button"
        ref={buttonRef}
        onClick={() => setOpen((o) => !o)}
        className={`relative p-1 rounded hover:bg-gray-100 ${isActive ? 'text-blue-600' : 'text-gray-500'}`}
        title="Filter"
      >
        {/* funnel icon */}
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
          <path d="M3 5h18l-7 8v6l-4 2v-8L3 5z" />
        </svg>
        {isActive && <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full"></span>}
      </button>
      {open && createPortal((
        <div
          ref={menuRef}
          className="z-50 bg-white border border-gray-200 rounded-lg shadow-xl p-3"
          style={{ position: 'fixed', top: coords.top, left: coords.left, width: coords.width, maxHeight: '70vh', overflow: 'auto' }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {!isCategorical && (
            <div className="mb-2">
              <select
                className="w-full h-9 px-3 text-sm border border-gray-300 rounded"
                value={op}
                onChange={(e) => setFilter({ op: e.target.value, value: null })}
              >
                {opsNumerical.map((o) => (
                  <option key={o.k} value={o.k}>{o.l}</option>
                ))}
              </select>
            </div>
          )}

          {/* Numerical operators */}
          {!isCategorical && op === 'between' && (
            <div className="space-y-2">
              <input
                type="number"
                inputMode="decimal"
                className="w-full h-9 px-3 text-sm border border-gray-300 rounded"
                placeholder="From"
                value={Array.isArray(value) ? value?.[0] ?? '' : ''}
                onChange={(e) => setFilter({ op, value: [e.target.value, Array.isArray(value) ? value?.[1] : ''] })}
              />
              <input
                type="number"
                inputMode="decimal"
                className="w-full h-9 px-3 text-sm border border-gray-300 rounded"
                placeholder="To"
                value={Array.isArray(value) ? value?.[1] ?? '' : ''}
                onChange={(e) => setFilter({ op, value: [Array.isArray(value) ? value?.[0] : '', e.target.value] })}
              />
            </div>
          )}
          {!isCategorical && op !== 'between' && op !== 'blank' && op !== 'notBlank' && (
            <input
              type="number"
              inputMode="decimal"
              className="w-full h-9 mt-2 px-3 text-sm border border-gray-300 rounded"
              placeholder="Value"
              value={value ?? ''}
              onChange={(e) => setFilter({ op, value: e.target.value })}
            />
          )}
          {/* Categorical multi-select */}
          {isCategorical && (
            <CategoricalOptions column={column} value={Array.isArray(value) ? value : []} onChange={(vals) => setFilter({ op: 'in', value: vals })} />
          )}
        </div>
      ), document.body)}
    </div>
  )
}

// Operator-based filter functions
const numericOperatorFilter = (row, columnId, filter) => {
  if (!filter) return true
  const v = row.getValue(columnId)
  const num = v == null ? null : Number(v)
  const op = filter.op
  const val = Array.isArray(filter.value) ? filter.value : [filter.value]
  if (op === 'blank') return v == null || v === ''
  if (op === 'notBlank') return !(v == null || v === '')

  // Column comparison logic
  if (op.endsWith('_col')) {
    const targetCol = filter.value
    if (!targetCol) return true
    const targetVal = row.getValue(targetCol)
    const targetNum = targetVal == null ? null : Number(targetVal)
    if (num == null || Number.isNaN(num) || targetNum == null || Number.isNaN(targetNum)) return false

    switch (op) {
      case 'gt_col': return num > targetNum
      case 'lt_col': return num < targetNum
      case 'eq_col': return Math.abs(num - targetNum) < 0.0001
      default: return true
    }
  }

  if (num == null || Number.isNaN(num)) return false
  const a = val?.[0] !== '' && val?.[0] != null ? Number(val[0]) : null
  const b = val?.[1] !== '' && val?.[1] != null ? Number(val[1]) : null
  switch (op) {
    case 'equals':
    case 'eq': return a != null && num === a
    case 'notEquals':
    case 'neq': return a != null && num !== a
    case 'gt': return a != null && num > a
    case 'gte': return a != null && num >= a
    case 'lt': return a != null && num < a
    case 'lte': return a != null && num <= a
    case 'between': return a != null && b != null && num >= a && num <= b
    default: return true
  }
}

const categoricalOperatorFilter = (row, columnId, filter) => {
  if (!filter) return true
  const v = row.getValue(columnId)
  const s = (v ?? '').toString().toLowerCase()
  const op = filter.op
  const q = (filter.value ?? '').toString().toLowerCase()
  if (op === 'blank') return !v
  if (op === 'notBlank') return !!v
  if (op === 'in') {
    const list = Array.isArray(filter.value) ? filter.value.map((x) => x.toString().toLowerCase()) : []
    return list.length === 0 ? true : list.includes(s)
  }
  switch (op) {
    case 'contains': return s.includes(q)
    case 'equals':
    case 'eq': return s === q
    case 'notEquals':
    case 'neq': return s !== q
    case 'startsWith': return s.startsWith(q)
    case 'endsWith': return s.endsWith(q)
    default: return true
  }
}

// Build unique categorical options from data for a column
const getCategoricalOptions = (rows, key, limit = 200) => {
  const set = new Set()
  for (const r of rows) {
    const v = r[key]
    if (v != null && v !== '') set.add(v)
    if (set.size > limit) break
  }
  return Array.from(set).sort()
}

const CategoricalOptions = ({ column, value, onChange }) => {
  const table = column.getTable?.() || null
  const rows = table?.options?.data || []
  const key = column.id
  const options = useMemo(() => getCategoricalOptions(rows, key, 500), [rows, key])
  const selected = new Set(Array.isArray(value) ? value : [])
  const [query, setQuery] = useState('')
  const filtered = options.filter((o) => o.toString().toLowerCase().includes(query.toLowerCase()))

  const toggle = (item) => {
    const next = new Set(selected)
    if (next.has(item)) next.delete(item); else next.add(item)
    onChange(Array.from(next))
  }

  return (
    <div>
      <input
        type="text"
        className="w-full h-9 mb-2 px-3 text-sm border border-gray-300 rounded"
        placeholder="Search..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <div className="max-h-60 overflow-auto space-y-1">
        <label className="flex items-center gap-2 text-sm px-1 py-0.5">
          <input
            type="checkbox"
            className="accent-blue-600"
            checked={selected.size === filtered.length}
            onChange={(e) => onChange(e.target.checked ? filtered : [])}
          />
          <span>(Select All)</span>
        </label>
        {filtered.map((opt) => (
          <label key={opt} className="flex items-center gap-2 text-sm px-1 py-0.5">
            <input
              type="checkbox"
              className="accent-blue-600"
              checked={selected.has(opt)}
              onChange={() => toggle(opt)}
            />
            <span className="truncate" title={opt}>{opt}</span>
          </label>
        ))}
      </div>
    </div>
  )
}

// --- Dynamic column generation helpers ---
const detectDataType = (key, sampleValue) => {
  if (key?.toLowerCase().includes('margin') || /^ch\d+[wmy]$/i.test(key)) {
    return 'percentage'
  }
  if (typeof sampleValue === 'number') return 'numerical'
  if (typeof sampleValue === 'string') {
    const maybeNum = Number(sampleValue)
    if (!Number.isNaN(maybeNum)) return 'numerical'
    return 'categorical'
  }
  return 'categorical'
}

const preferredOrder = [
  'symbol', 'name', 'exchange', 'country', 'sector', 'industry',
  'close_price', 'close', 'volume', 'averagevolume',
  'revenue', 'market_cap', 'marketcap',
  'gross_margin', 'operating_margin', 'profit_margin',
  'pe_ratio', 'peratio', 'pe_forward',
  'debt', 'employees',
  'ch1w', 'ch1m', 'ch3m', 'ch6m', 'ch1y'
]

const StockScreenerTable = ({ externalFilters = [], onFieldsReady }) => {
  const [data, setData] = useState([])
  const [metadata, setMetadata] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [refreshing, setRefreshing] = useState(false)
  const [columnFilters, setColumnFilters] = useState([])
  const [sorting, setSorting] = useState([])
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  })
  const [columnVisibility, setColumnVisibility] = useState(() => {
    try {
      const saved = localStorage.getItem('traderbro_screener_column_visibility')
      return saved ? JSON.parse(saved) : {}
    } catch (e) {
      console.error('Failed to parse column visibility from localStorage', e)
      return {}
    }
  })

  // Persist column visibility
  useEffect(() => {
    localStorage.setItem('traderbro_screener_column_visibility', JSON.stringify(columnVisibility))
  }, [columnVisibility])

  // Load data on component mount
  useEffect(() => {
    loadScreenerData({ showSpinner: true })
  }, [])

  const loadScreenerData = async ({ showSpinner } = { showSpinner: true }) => {
    try {
      if (showSpinner) setLoading(true)
      setError(null)
      const response = await fetchAllScreenerData()

      if (response.status === 'success') {
        setData(response.data || [])
        if (response.metadata) {
          setMetadata(response.metadata)
        }
      } else {
        setError(response.error || 'Failed to load data')
      }
    } catch (err) {
      setError('Failed to fetch screener data')
      console.error('Error loading data:', err)
    } finally {
      if (showSpinner) setLoading(false)
    }
  }

  const handleRefreshCache = async () => {
    // Keep table visible; only show small spinner on button
    setRefreshing(true)
    try {
      await refreshScreenerCache()
    } catch (err) {
      // Non-admin users will get 403. Fall back to just refetching data.
      console.warn('Refresh cache endpoint failed (continuing with soft refresh):', err?.response?.status)
    } finally {
      await loadScreenerData({ showSpinner: false })
      setRefreshing(false)
    }
  }

  // Column definitions — built dynamically from the first row keys
  const columns = useMemo(() => {
    if (!data || data.length === 0) return []
    const sample = data[0] || {}
    // Determine order: preferred keys first, then remaining alphabetically
    // Use all keys present in the first row; includes everything the backend sends
    const keys = Object.keys(sample)
      // place preferred keys first while preserving all others afterwards
      .sort((a, b) => {
        const ia = preferredOrder.indexOf(a)
        const ib = preferredOrder.indexOf(b)
        if (ia === -1 && ib === -1) return a.localeCompare(b)
        if (ia === -1) return 1
        if (ib === -1) return -1
        return ia - ib
      })

    const colDefs = keys.map((key) => {
      const sampleVal = sample[key]
      const type = detectDataType(key, sampleVal)

      // Use metadata label if available, otherwise fallback to snake_case capitalization
      const colMeta = metadata[key] || {}
      const headerLabel = colMeta.label || key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())

      const header = () => (
        <div className="flex items-center gap-1">
          <span>{headerLabel}</span>
        </div>
      )

      const cell = ({ row, getValue }) => {
        const val = getValue()
        if (key === 'symbol') {
          return <span className="font-mono font-semibold text-blue-600">{val}</span>
        }
        if (key === 'close_price' || key === 'close') {
          const v = val != null ? val : row.original?.close
          return <span className="font-mono">{v != null ? `$${Number(v).toFixed(2)}` : '-'}</span>
        }
        if (key === 'volume' || key === 'averagevolume') {
          const v = val != null ? val : row.original?.averagevolume
          return formatNumber(v)
        }
        if (key === 'revenue' || key === 'market_cap' || key === 'marketcap' || key === 'debt') {
          const v = val != null ? val : (key === 'revenue' ? row.original?.market_cap : val)
          return formatCurrency(v)
        }
        if (type === 'percentage') return formatPercent(Number(val))
        if (type === 'numerical') return formatNumber(Number(val))
        return <div className="max-w-[260px] truncate" title={val}>{val}</div>
      }

      return {
        accessorKey: key,
        header,
        headerLabel,
        cell,
        size: key === 'name' ? 220 : key === 'symbol' ? 100 : 140,
        meta: { dataType: type },
        filterFn: type === 'numerical' || type === 'percentage' ? 'numericOp' : 'categoricalOp',
      }
    })

    // expose dynamic field list to parent (filters component)
    if (onFieldsReady) {
      const fields = keys.map((key) => {
        const t = detectDataType(key, sample[key])
        const label = key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
        const normalizedType = t === 'numerical' || t === 'percentage' ? 'numerical' : 'categorical'
        let options = undefined
        if (normalizedType === 'categorical') {
          const set = new Set()
          for (const r of data) {
            const v = r[key]
            if (v != null && v !== '') set.add(v)
          }
          options = Array.from(set).sort()
        }
        return { key, label, type: normalizedType, options }
      })
      // fire asynchronously to avoid render loops
      Promise.resolve().then(() => onFieldsReady(fields))
    }

    return colDefs
  }, [data])

  // derive tanstack columnFilters from external filters
  useEffect(() => {
    if (!data || data.length === 0) return
    const next = externalFilters.map((f) => ({ id: f.key, value: f }))
    setColumnFilters(next)
  }, [externalFilters, data])

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      columnFilters,
      sorting,
      pagination,
      columnVisibility,
    },
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onColumnVisibilityChange: setColumnVisibility,
    enableMultiSort: true,
    filterFns: {
      numericOp: numericOperatorFilter,
      categoricalOp: categoricalOperatorFilter,
    },
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading stock data...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-red-600">
          <p className="text-lg font-semibold">Error loading data</p>
          <p className="text-sm">{error}</p>
          <button
            onClick={loadScreenerData}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-white overflow-hidden min-w-0">
      {/* Scroll container for header, table and sticky pagination */}
      <div className="flex-1 overflow-auto pb-20">
        {/* Table header (non-sticky to avoid overlapping other UI) */}
        <div className="p-4 border-b border-gray-200 bg-white">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">
                Stock Screener Table
              </h2>
              <p className="text-sm text-gray-600">
                {table.getFilteredRowModel().rows.length} of {data.length} stocks
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <ColumnConfigPopover
                columns={table.getAllColumns().map(c => ({ id: c.id, headerLabel: c.columnDef.headerLabel || c.id }))}
                visibility={columnVisibility}
                onVisibilityChange={setColumnVisibility}
              />
              <button
                onClick={handleRefreshCache}
                className={`px-3 py-1 text-sm rounded border ${refreshing ? 'bg-gray-200 text-gray-500' : 'bg-gray-100 hover:bg-gray-200'}`}
                disabled={refreshing}
                title={refreshing ? 'Refreshing...' : 'Rebuild cache and reload data'}
              >
                {refreshing ? 'Refreshing…' : 'Refresh Data'}
              </button>
              <button
                onClick={() => {
                  setColumnFilters([])
                  setSorting([])
                }}
                className="px-3 py-1 text-sm bg-blue-100 hover:bg-blue-200 text-blue-700 rounded border border-blue-300"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
        <div className="w-full overflow-x-auto">
          <table className="min-w-[1600px] w-full table-fixed">
            <thead className="bg-gray-50 sticky top-0 z-10">
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      className="px-3 py-2 text-left align-top text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200"
                      style={{ width: header.getSize() }}
                    >
                      <div className="space-y-1">
                        {/* Header with sorting */}
                        {header.isPlaceholder ? null : (
                          <div
                            className={`flex items-center gap-1 ${header.column.getCanSort() ? 'cursor-pointer select-none' : ''
                              }`}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: ' 🔼',
                              desc: ' 🔽',
                            }[header.column.getIsSorted()] ?? null}
                          </div>
                        )}

                        {/* No inline filter inputs; sorting only */}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {table.getRowModel().rows.map(row => (
                <tr
                  key={row.id}
                  className="hover:bg-gray-50 transition-colors"
                >
                  {row.getVisibleCells().map(cell => (
                    <td
                      key={cell.id}
                      className="px-3 py-2 whitespace-nowrap text-sm text-gray-900"
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Sticky bottom pagination inside the scroll container */}
        <div className="sticky bottom-0 z-10 p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700">
                Page {table.getState().pagination.pageIndex + 1} of{' '}
                {table.getPageCount()}
              </span>
              <select
                value={table.getState().pagination.pageSize}
                onChange={e => {
                  table.setPageSize(Number(e.target.value))
                }}
                className="px-2 py-1 text-sm border border-gray-300 rounded"
              >
                {[25, 50, 100, 200].map(pageSize => (
                  <option key={pageSize} value={pageSize}>
                    Show {pageSize}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
                className="px-2 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {'<<'}
              </button>
              <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="px-2 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {'<'}
              </button>
              <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="px-2 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {'>'}
              </button>
              <button
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
                className="px-2 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {'>>'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default StockScreenerTable
